// SPEC-OCR-STAGED-PROD-RELEASE-V1 (D-427) ITEM_2: 実行時フィーチャーフラグ(全社一斉スイッチ + キルスイッチ)。
//   プログレッシブデリバリー標準: ロールアウト制御をビルド時定数でなく実行時フラグに置き、キルスイッチをデプロイ非依存に。
//   フェイルセーフ既定: 取得失敗時は直前の既知値を保持し、既知値が無ければ安全側(fallback)へ倒す。
import { supabase } from './supabase'
import { putAppState, getAppState } from './localStore/appState'

const APP_STATE_KEY = 'feature_flags'

// メモリキャッシュ。null = 未ロード(この間は fallback を返す)。{ [flag_key]: boolean } = ロード済み。
let cache = null

// 同期。キャッシュに当該キーがあればその値、無ければ fallback。撮影開始時など同期評価点で使う。
export function isFeatureEnabled(key, fallback = false) {
  if (cache && Object.prototype.hasOwnProperty.call(cache, key)) return !!cache[key]
  return !!fallback
}

// 非同期。supabase から feature_flags を select。エラー時・0行時はキャッシュを一切上書きしない
//   (未認証やオフラインで空配列が返る場合にフラグが消えたと誤認して全社設定を落とすのを防ぐ)。1行以上のときだけ上書き。
export async function refreshFeatureFlags() {
  let res
  try {
    // full-scan-intentional: feature_flags は全社フラグの少数行(現状7行)。全件取得して同期キャッシュに載せるのが正。
    res = await supabase.from('feature_flags').select('flag_key, enabled')
  } catch {
    return // ネットワーク例外等: キャッシュ据え置き
  }
  if (res?.error) return
  const data = res?.data
  if (!Array.isArray(data) || data.length === 0) return // 0行/未認証: 上書きしない
  const next = {}
  for (const row of data) {
    if (row && row.flag_key != null) next[row.flag_key] = !!row.enabled
  }
  cache = next
  await putAppState(APP_STATE_KEY, next)
}

// 非同期。app state から復元し、メモリキャッシュが空のときだけ埋める(refresh が来るまでの初期値)。
export async function hydrateFeatureFlags() {
  if (cache) return
  const stored = await getAppState(APP_STATE_KEY)
  if (cache == null && stored && typeof stored === 'object') cache = stored
}

// キャッシュがロード済みか(ITEM_3 のリトライ打切り判定に使う内部ヘルパー)。
function isLoaded() { return cache != null }

// SPEC-FEATURE-FLAG-REACHABILITY-V1 (D-428): フラグ到達性。起動しただけ(背面復帰なし)でも届き、キルスイッチの到達に
//   上限を与える。main.jsx から1回だけ呼ぶ。D-427 の起動時 hydrate/refresh とフェイルセーフ規則(error/0行で上書きしない)は不変。
//   ITEM_1 認証確立での再取得 / ITEM_2 前面ポーリング(到達上限=5分) / ITEM_3 起動直後の短期リトライ。
//   依存はテスト容易化のため注入可(既定は本番の supabase.auth / document / navigator / グローバルタイマ)。
export function startFeatureFlagsSync(deps = {}) {
  const authClient = deps.auth ?? supabase?.auth
  const doc = deps.doc ?? (typeof document !== 'undefined' ? document : null)
  const nav = deps.nav ?? (typeof navigator !== 'undefined' ? navigator : null)
  const setIv = deps.setInterval ?? ((typeof setInterval !== 'undefined') ? setInterval : null)
  const clearIv = deps.clearInterval ?? ((typeof clearInterval !== 'undefined') ? clearInterval : null)
  const setTo = deps.setTimeout ?? ((typeof setTimeout !== 'undefined') ? setTimeout : null)
  const intervalMs = deps.intervalMs ?? 5 * 60 * 1000
  const retryDelays = deps.retryDelays ?? [3000, 10000, 30000]
  const refresh = deps.refresh ?? refreshFeatureFlags

  // ITEM_1: 認証確立で再取得。session 非null の INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED のみ。session null では
  //   叩かない(anon は 0 行しか返らず無駄)。購読解除はしない(アプリ生存中ずっと有効)。
  authClient?.onAuthStateChange?.((event, session) => {
    if (!session) return
    if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') refresh()
  })

  // ITEM_2: 前面ポーリング。前面のとき intervalMs(5分)間隔で、各発火はオンライン時のみ refresh(圏外は叩かない)。
  //   背面で停止・前面で再開。これがキルスイッチの到達上限を 5 分にする(本 D の中心)。
  let poll = null
  const online = () => nav == null || nav.onLine !== false
  const startPoll = () => {
    if (poll != null || setIv == null) return
    poll = setIv(() => { if (online()) refresh() }, intervalMs)
  }
  const stopPoll = () => { if (poll != null && clearIv) { clearIv(poll); poll = null } }
  if (doc) {
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'visible') startPoll(); else stopPoll()
    })
    if (doc.visibilityState === 'visible') startPoll()
  } else {
    startPoll()
  }

  // ITEM_3: 起動直後の refresh が空振り(キャッシュ空)のときの短期リトライ。3s/10s/30s の最大3回、成功で打切り、無限にしない。
  //   ITEM_1 で埋まった場合も先頭の isLoaded() ガードで叩かない(保険)。
  const scheduleRetry = (i) => {
    if (i >= retryDelays.length || setTo == null) return
    setTo(async () => {
      if (isLoaded()) return
      await refresh()
      if (!isLoaded()) scheduleRetry(i + 1)
    }, retryDelays[i])
  }
  scheduleRetry(0)
}

// テスト専用: キャッシュを直接セット/クリアする(本番コードからは呼ばない)。
export function __setFeatureFlagsCacheForTest(v) { cache = v }
